import { useMemo } from "react";
import { useQuery, useQueries } from "react-query";

import {
  dicomOptions,
  fetchSeries,
  fetchSeriesMetadata,
  searchPatientStudies,
} from "lib/client/dicomweb";
import safeNaturalizeDataset from "lib/cornerstone/helpers/safeNaturalizeDataset";
import { transformPath } from "lib/cornerstone/helpers/niftiDiscovery";
import { ENTRYPOINT } from "config/api";
import { parseQuantitativeDataFromSR } from "./useMarkers";
import useViewerParams from "./useViewerParams";

export interface LongitudinalTimepoint {
  /** YYYYMMDD */
  studyDate: string;
  /** Same shape as parseQuantitativeDataFromSR: [code, value, unit][] */
  quantification: [string, number, string][];
}

export interface LongitudinalResult {
  timepoints: LongitudinalTimepoint[];
  isDone: boolean;
  patientStudies?: any[];
  /**
   * DICOM only. NormativeGraph reads the raw SR instances directly
   * (per-timepoint age + per-marker icv).
   */
  studiesInstanceMetadata?: any[];
  /** DICOM readiness flag for NormativeGraph (separate from NIfTI `isDone`). */
  studiesIsDone?: boolean;
}

// Region name → WMH RID (matches roiSummary "WMH" sub_rois)
const WMH_REGION_TO_CODE: Record<string, string> = {
  Periventricular: "RID39467:RID6384",
  Deep: "RID39467:RID6387",
  Cortical: "RID39467:RID45765",
  Juxtacortical: "RID39467:RID45765",
  Infratentorial: "RID39467:RID6381",
};

// ───────────────────────────── DICOM path ─────────────────────────────

export function useLongitudinalStudies(enabled = true) {
  const { patient_id } = useViewerParams();
  const { data: datasets } = useQuery(
    ["studies", patient_id],
    ({ queryKey }) => searchPatientStudies(queryKey[1]!),
    { ...dicomOptions, enabled: enabled && !!patient_id },
  );
  const patientStudies = useMemo(
    () => datasets?.map(safeNaturalizeDataset),
    [datasets],
  );
  return { patientStudies };
}

function useLongitudinalDicom(enabled: boolean): LongitudinalResult {
  const { patient_id } = useViewerParams();
  const { patientStudies } = useLongitudinalStudies(enabled);

  const patientStudiesUID = patientStudies?.map(
    (study) => study.StudyInstanceUID,
  );

  const studiesSeriesQueries = useQueries(
    patientStudiesUID?.map((studyUID) => ({
      queryKey: ["studies", studyUID, "series", patient_id],
      queryFn: () => fetchSeries(studyUID, patient_id),
      enabled: enabled && !!studyUID,
      ...dicomOptions,
    })) || [],
  );

  const seriesInfo = useMemo(
    () =>
      studiesSeriesQueries?.map((study) =>
        study.data?.map(safeNaturalizeDataset),
      ),
    [studiesSeriesQueries],
  );

  const SRSeriesInfo = seriesInfo
    ?.map((d) => d?.find((serie) => serie.Modality === "SR"))
    .filter(Boolean);

  const SRMetadatas = useQueries(
    SRSeriesInfo.map((series) => ({
      queryKey: [
        "studies",
        series?.StudyInstanceUID,
        "series",
        series?.SeriesInstanceUID,
        "metadata",
      ],
      queryFn: () =>
        fetchSeriesMetadata(
          series?.StudyInstanceUID,
          series?.SeriesInstanceUID,
        ),
      enabled: enabled && !!SRSeriesInfo?.every((d) => !!d),
      ...dicomOptions,
    })) || [],
  );

  const studiesInstanceMetadata = SRMetadatas?.map((d) =>
    d.data?.map(safeNaturalizeDataset),
  );

  const timepoints: LongitudinalTimepoint[] = (studiesInstanceMetadata ?? [])
    .filter(Boolean)
    .map((instances: any) => ({
      studyDate: instances?.[0]?.StudyDate,
      quantification: parseQuantitativeDataFromSR(instances),
    }))
    .filter((tp) => tp.studyDate)
    .sort((a, b) => String(a.studyDate).localeCompare(String(b.studyDate)));

  const isDone = SRMetadatas?.every((d) => d.isFetched && !d.error);

  return { timepoints, isDone, patientStudies, studiesInstanceMetadata };
}

// ───────────────────────────── NIfTI path ─────────────────────────────

function useLongitudinalNifti(enabled: boolean): LongitudinalResult {
  const { patient_id, acquisition } = useViewerParams();
  // Same baseUrl the discovery uses (see Viewer.tsx → discoverNiftiFiles(ENTRYPOINT, ...))
  const niftiBaseUrl = ENTRYPOINT;

  // 1. List disk items for the current acquisition (the patient-wide endpoint
  //    without an acquisition segment is not available). This response still
  //    enumerates every ses of the patient (clinical_info / wmh json per ses).
  const { data: paths } = useQuery(
    ["disk_items", patient_id, acquisition],
    async () => {
      const resp = await fetch(
        `${niftiBaseUrl}/patients/${patient_id}/disk_items/${acquisition}`,
        { credentials: "include" },
      );
      if (!resp.ok) throw new Error(`disk_items ${resp.status}`);
      const j = await resp.json();
      return (
        Array.isArray(j) ? j : j.items ?? j.disk_items ?? j.files ?? []
      ) as string[];
    },
    { enabled: enabled && !!patient_id && !!acquisition },
  );

  // 2. Build a wmh-quantification path for EVERY ses found in the listing.
  //    The path differs across timepoints only by the ses token, so we take
  //    one wmh json as a template and swap in each ses.
  const allPaths = paths ?? [];
  const wmhTemplate = allPaths.find(
    (p) => /_wmh_quantification\.json$/.test(p) && /ses-\d{8}/.test(p),
  );
  // Catch every ses token anywhere, including inside "ses-A_to_ses-B" folders.
  const sesList = Array.from(
    new Set(allPaths.flatMap((p) => p.match(/ses-\d{8}/g) ?? [])),
  );
  const wmhJsonPaths = wmhTemplate
    ? sesList.map((ses) => wmhTemplate.replace(/ses-\d{8}/, ses))
    : [];

  // 3. Fetch + parse each json into the unified timepoint shape
  const jsonQueries = useQueries(
    wmhJsonPaths.map((rawPath) => {
      const ses = rawPath.match(/\/(ses-\d{8})\//)?.[1] ?? "";
      const studyDate = ses.replace("ses-", "");
      return {
        queryKey: ["wmh_quant", rawPath],
        queryFn: async (): Promise<LongitudinalTimepoint> => {
          // Same disk-item content URL convention the viewer uses for nii.gz/json.
          const url = `${niftiBaseUrl}${transformPath(rawPath)}`;
          const r = await fetch(url, { credentials: "include" });
          if (!r.ok) throw new Error(`wmh json ${r.status}`);
          const j = await r.json();
          const quantification: [string, number, string][] = Object.entries(
            j.WMH ?? {},
          )
            .filter(([region]) => WMH_REGION_TO_CODE[region])
            .map(([region, v]: any) => [
              WMH_REGION_TO_CODE[region],
              v.volume_ml, // mL (1 cm3 == 1 mL); matches DICOM SR "cm3" unit
              "cm3",
            ]);
          return { studyDate, quantification };
        },
        enabled: enabled,
      };
    }),
  );

  // Dedupe by date via Map — one timepoint per distinct ses, sorted ascending.
  const byDate = new Map<string, LongitudinalTimepoint>();
  for (const q of jsonQueries) {
    if (q.data?.studyDate) byDate.set(q.data.studyDate, q.data);
  }
  const timepoints = Array.from(byDate.values()).sort((a, b) =>
    a.studyDate.localeCompare(b.studyDate),
  );

  // Render as soon as nothing is still loading — a single failing ses must not
  // blank the whole graph (errored queries count as "done", just without data).
  const isDone =
    wmhJsonPaths.length > 0 && jsonQueries.every((q) => !q.isLoading);

  return {
    timepoints,
    isDone,
    patientStudies: undefined,
    studiesInstanceMetadata: undefined,
  };
}

// ───────────────────────────── Unified entry ─────────────────────────────

function useLongitudinal(): LongitudinalResult {
  // Hybrid environment: DICOM SR (normative) and NIfTI files (longitudinal) can
  // coexist, and instanceMetadata / niftiBases are not reliable mode flags in
  // every view. So we DON'T switch — we run both and expose both data sources.
  // Each graph reads the field it needs:
  //   - LongitudinalGraph → timepoints / isDone   (NIfTI)
  //   - NormativeGraph     → studiesInstanceMetadata / studiesIsDone (DICOM) TODO: change to NifTi
  const nifti = useLongitudinalNifti(true);
  const dicom = useLongitudinalDicom(true);

  return {
    // NIfTI → LongitudinalGraph
    timepoints: nifti.timepoints,
    isDone: nifti.isDone,
    // DICOM → NormativeGraph
    studiesInstanceMetadata: dicom.studiesInstanceMetadata,
    patientStudies: dicom.patientStudies,
    studiesIsDone: dicom.isDone,
  };
}

export default useLongitudinal;
